import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import * as React from "react";

import { Layout } from "~landing/components/layout";
import { Faqs } from "~landing/components/pricing/faqs";
import { FeaturesSection } from "~landing/components/pricing/features";
import { PlansTable } from "~landing/components/pricing/plans-table";
import { StartBuilding } from "~landing/components/pricing/start-building";

const blurBandHeight = "240px";
const pct = (value: number) => `${value}%`;

function createSliceMask(
	start: number,
	peak: number,
	hold: number,
	end?: number,
) {
	if (end === undefined) {
		return `linear-gradient(to bottom, transparent 0%, transparent ${pct(start)}, rgba(0, 0, 0, 0.72) ${pct(peak)}, black ${pct(hold)}, black 100%)`;
	}

	return `linear-gradient(to bottom, transparent 0%, transparent ${pct(start)}, rgba(0, 0, 0, 0.72) ${pct(peak)}, black ${pct(hold)}, transparent ${pct(end)}, transparent 100%)`;
}

const blurLayers = [
	{ blur: "0.125px", mask: createSliceMask(0, 12.5, 25, 37.5) },
	{ blur: "0.25px", mask: createSliceMask(12.5, 25, 37.5, 50) },
	{ blur: "0.5px", mask: createSliceMask(25, 37.5, 50, 62.5) },
	{ blur: "1px", mask: createSliceMask(37.5, 50, 62.5, 75) },
	{ blur: "2px", mask: createSliceMask(50, 62.5, 75, 87.5) },
	{ blur: "4px", mask: createSliceMask(62.5, 75, 87.5, 100) },
	{ blur: "8px", mask: createSliceMask(75, 87.5, 100) },
	{ blur: "16px", mask: createSliceMask(87.5, 93.75, 100) },
] as const;

export function PricingView() {
	const footerExclusionRef = React.useRef<HTMLDivElement | null>(null);
	const [shouldDisableViewportBlur, setShouldDisableViewportBlur] =
		React.useState(false);

	React.useEffect(() => {
		const sentinelElement = footerExclusionRef.current;

		if (!sentinelElement || typeof IntersectionObserver === "undefined") {
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				setShouldDisableViewportBlur(entry?.isIntersecting ?? false);
			},
			{
				root: null,
				rootMargin: `0px 0px -${blurBandHeight} 0px`,
				threshold: 0,
			},
		);

		observer.observe(sentinelElement);

		return () => {
			observer.disconnect();
		};
	}, []);

	return (
		<Layout>
			<Box
				component="main"
				sx={{
					bgcolor: "var(--mui-palette-background-level1)",
					isolation: "isolate",
					mt: "calc(var(--MainNav-height) * -1)",
					position: "relative",
				}}>
				<Box sx={{ position: "relative", zIndex: 1 }}>
					<PlansTable />
					<Divider />
					<FeaturesSection />
					<Faqs />
					<StartBuilding />
					<Box
						ref={footerExclusionRef}
						sx={{ height: "1px", pointerEvents: "none", width: "100%" }}
					/>
				</Box>
				<Box
					sx={{
						bottom: 0,
						display: { xs: "none", sm: "block" },
						height: blurBandHeight,
						left: 0,
						opacity: shouldDisableViewportBlur ? 0 : 1,
						pointerEvents: "none",
						position: "fixed",
						right: 0,
						transition: shouldDisableViewportBlur
							? "opacity 180ms ease, visibility 0ms linear 180ms"
							: "opacity 180ms ease, visibility 0ms linear 0ms",
						visibility: shouldDisableViewportBlur ? "hidden" : "visible",
						zIndex: 2,
					}}>
					{blurLayers.map((layer) => (
						<Box
							key={layer.blur}
							sx={{
								WebkitBackdropFilter: `blur(${layer.blur})`,
								WebkitMaskImage: layer.mask,
								backdropFilter: `blur(${layer.blur})`,
								backgroundColor: "rgba(255, 255, 255, 0.01)",
								inset: 0,
								maskImage: layer.mask,
								pointerEvents: "none",
								position: "absolute",
							}}
						/>
					))}
				</Box>
				<Box
					sx={{
						backgroundImage: 'url("/app-assets/images/noise.webp")',
						backgroundRepeat: "repeat",
						backgroundSize: "180px 180px",
						inset: 0,
						opacity: 0.08,
						pointerEvents: "none",
						position: "fixed",
						userSelect: "none",
						zIndex: 3,
					}}
				/>
			</Box>
		</Layout>
	);
}
